import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Divider, Alert, CircularProgress
} from "@mui/material"
import { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { flagForReview, unflagAccount } from "../../redux/admin-slices/wallet.slice"

/**
 * FlagUnflagDialog
 *
 * Flag a specialist for review (auto-pauses payouts) or remove the flag.
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   mode       — "flag" | "unflag"
 *   specialist — { _id, fullName, email, flaggedForReview, flagReason, flaggedAt }
 *   onSuccess  — () => void
 */
export default function FlagUnflagDialog({ open, onClose, mode = "flag", specialist, onSuccess }) {
  const dispatch   = useDispatch()
  const [reason, setReason]   = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState("")

  useEffect(() => {
    if (open) { setReason(""); setError("") }
  }, [open])

  const isFlag = mode === "flag"

  const handleSubmit = async () => {
    if (isFlag && reason.trim().length < 10) {
      setError("Please provide a reason (min 10 characters).")
      return
    }
    setLoading(true)
    try {
      const action = isFlag
        ? flagForReview({ specialistId: specialist._id, reason: reason.trim() })
        : unflagAccount({ specialistId: specialist._id })
      const result = await dispatch(action).unwrap()
      if (result) { onSuccess?.(); onClose() }
    } catch {
      // toast shown by thunk
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={!loading ? onClose : undefined} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>
        {isFlag ? "🚩 Flag for Review" : "✅ Remove Flag"}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 2, p: 1.5, bgcolor: "#f5f5f5", borderRadius: 2 }}>
          <Typography fontWeight={600} textTransform="capitalize">{specialist?.fullName}</Typography>
          <Typography color="text.secondary" fontSize={13}>{specialist?.email}</Typography>
        </Box>

        {isFlag ? (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              Flagging this account will <strong>automatically pause payouts</strong>.
              This is intended for dispute investigation or suspected fraud.
              You must resolve the flag before payouts can resume.
            </Alert>
            <TextField
              label="Flag Reason *"
              fullWidth multiline rows={3}
              value={reason}
              onChange={(e) => { setReason(e.target.value); setError("") }}
              error={Boolean(error)}
              helperText={error || "Describe the reason for flagging (min 10 chars). Stored against this wallet."}
              inputProps={{ maxLength: 500 }}
            />
          </>
        ) : (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Removing the flag does <strong>not</strong> automatically resume payouts.
              You will need to separately click <strong>"Resume Payouts"</strong> after unflagging.
            </Alert>
            {specialist?.flagReason && (
              <Box sx={{ p: 1.5, bgcolor: "#fff3e0", borderRadius: 2, mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  CURRENT FLAG REASON:
                </Typography>
                <Typography fontSize={14} mt={0.5}>{specialist.flagReason}</Typography>
                {specialist?.flaggedAt && (
                  <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
                    Flagged on: {new Date(specialist.flaggedAt).toLocaleString()}
                  </Typography>
                )}
              </Box>
            )}
            <Alert severity="info" sx={{ mt: 1 }}>
              Confirm you have investigated this account and it is safe to remove the flag.
            </Alert>
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined" color="inherit">Cancel</Button>
        <Button
          variant="contained"
          color={isFlag ? "error" : "success"}
          disabled={loading}
          onClick={handleSubmit}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ minWidth: 130 }}
        >
          {loading ? "Saving…" : isFlag ? "Flag Account" : "Remove Flag"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
